import * as React from 'react';
import * as Moment from 'moment';
const classNames = require('classnames');
const _ = require('lodash');

interface RowLike {
    index: number;
    level: number;
    value: string | number | {};        // string, number or some kind of object
}

function clamp(v: number, lower: number, upper: number): number {
    return (v < lower)
        ? lower
        : (v > upper)
            ? upper
            : v;
}

/* Renderer functions can take supported Table callback object directly. This function performs the detection
   of the type and how to extract the actual value. The body of the renderer is provided by a lambda (second
   function).
 */
function expectString(value: RowLike | string | ArrayLike<string>, renderer: (value: string) => React.ReactNode, invalidValue ?: (value: any) => React.ReactNode): React.ReactNode {
    if (value === undefined || value === null)
        return invalidValue ? invalidValue(value) : <span className="zero-invalid" />;
    else if (Array.isArray(value)) {
        return _(value)
            .uniq()
            .map( (v: string) => renderer(v))
            .join();
    } else if (typeof value === 'number')
        return renderer(String(Math.round(value)));
    else if (typeof value === 'string')
        return renderer(value);
    else if ((value as RowLike).value !== undefined) {
        return (typeof (value as RowLike).value === 'string')
            ? renderer((value as RowLike).value as string)
            : (invalidValue ? invalidValue(value) : <span className="invalid expected-object"/>);
    } else
        return null;
}

function expectNumeric(value: RowLike | number | ArrayLike<number>, renderer: (value: number) => React.ReactNode, invalidValue ?: (value: any) => React.ReactNode): React.ReactNode {
    let n: number;
    if (value === undefined || value === null )
        return invalidValue ? invalidValue(value) : <span className="zero-invalid" />;
    else if (Array.isArray(value))
        return <span className="multi-value" />;    // todo: can we do aggregation here?
    else if (!isNaN(n = Number(value)))
        return renderer(n);
    else if ((value as RowLike).value !== undefined && !isNaN(n = Number((value as RowLike).value)))
        return renderer(n);
    else
        return invalidValue ? invalidValue(value) : <span className="invalid expected-numeric" />;
}

function expectAny(value: any, validate: (value: any) => boolean, renderer: (value: any) => React.ReactNode, invalidValue ?: (value: any) => React.ReactNode): React.ReactNode {
    if (value === undefined || value === null)
        return invalidValue ? invalidValue(value) : <span className="zero-invalid" />;
    else if (Array.isArray(value))       // aggregated view of objects
        return <span className="multi-value" />;    // todo: can we do aggregation here?
    else if (validate(value))    // check for expected object
        return renderer(value);
    else if (value.value !== undefined && value.value !== null && validate(value.value))    // check for react-table
        return renderer(value.value);
    else
        return invalidValue ? invalidValue(value) : <span className="invalid" />;
}

function expect<T>(value: RowLike | T, validate: (value: T) => boolean, renderer: (value: T) => React.ReactNode, invalidValue ?: (value: any) => React.ReactNode): React.ReactNode {
    let row = value as RowLike;
    if (value === undefined || value === null)
        return invalidValue ? invalidValue(value) : <span className="zero-invalid" />;
    else if (Array.isArray(value))       // aggregated view of objects
        return <span className="multi-value" />;    // todo: can we do aggregation here?
    else if (validate(value as T))    // check for expected object
        return renderer(value as T);
    else if (row.value !== undefined && row.value !== null && validate(row.value as T))    // check for react-table
        return renderer(row.value as T);
    else
        return invalidValue ? invalidValue(value) : <span className="invalid" />;
}

export module Renderers {
    // Subtly show the statistic value
    // Returns the argument as-is, unless it's zero then it returns a dash. Many statistics on a page are 0, rather
    // than busy up the page with 0's lets just show numbers that are non-zero to draw attention more to them.
    export function subtle(value: any, precision ?: number): React.ReactNode {
        return expectNumeric(value, (v) => {
            let n: number;
            return (v === 0)
                ? <span className="zero">-</span>
                : (isNaN(n = Number(v)))
                    ? v
                    : n.toFixed(precision ? precision : 0);
        });
    }

    export function ratio(value: any, precision ?: number): React.ReactNode {
        return expectNumeric(value, (v) => {
            return (v === 0)
                ? <span className="zero">-</span>
                : v.toFixed(precision ? precision : 2);
        });
    }

    interface SampleAggregate {
        samples: number;
        min: number;
        max: number;
        current: number;
        avg: number;
    }

    interface Http {
        hss2xx: number;
        hsr3xxTotal: number;
        hsce4xxTotal: number;
        hsse5xxTotal: number;
        close: number;
        socketErrors: number;
    }
    // tslint:disable-next-line:class-name
    export class timing {
        static millisLabel( v: number): string {
            return this.microsLabel(v * 1000);
        }

        static millis( v: number): React.ReactNode {
            return this.micros(v * 1000);
        }

        static microsLabel( v: number): string {
            let unit = 'uS';
            let precision = 1;
            if (v === 0) {
                unit = '';
                precision = -1;
                return '-';
            } else if (v >= 1000000) {
                v /= 1000000;
                unit = 's';
            } else if (v >= 1000) {
                v /= 1000;
                unit = 'ms';
            }
            if (v < 10) precision++;
            return v.toFixed(precision) + unit;
        }

        static micros( value: RowLike | number): React.ReactNode {
            return expectNumeric(value, v => {
                let unit = 'uS';
                let precision = 1;
                if (v === 0) {
                    unit = '';
                    precision = -1;
                    return <span className="time time-zero" />;
                } else if (v > 1000000) {
                    v /= 1000000;
                    unit = 's';
                } else if (v > 1000) {
                    v /= 1000;
                    unit = 'ms';
                }
                if (v < 10) precision++;
                return <span className={`time time-${unit}`}><h1>{v.toFixed(precision)}</h1><h2>{unit}</h2></span>;
            });
        }

        static request(value: any ): React.ReactNode {
            return expectAny(value, v => v.dnsTime !== undefined && v.connTime !== undefined && v.dlTime !== undefined, v => {
                return (
                    <div
                        className={`timing millis totals`}
                        title="DNS, connection and download timing in milliseconds"
                    >
                        <div className="dns-time">{Math.round(v.dnsTime / 1000)}</div>
                        <div className="connection-time">{Math.round(v.connTime / 1000)}</div>
                        <div className="download-time">{Math.round(v.dlTime)}</div>
                        <div className="unit">ms</div>
                    </div>
                );
            });
        }

        static minimaxHeader(title: string, scale: string, limit: number): React.ReactNode {
            return (
                <div className="minimax -header">
                    <small>{title}</small>
                    <div className="indicator">
                        <span className="scale">{scale}</span>
                        <span className="min">0uS</span>
                        <span className="max">{Renderers.timing.microsLabel(limit)}</span></div>
                </div>
            );
        }

        static minimax(value: RowLike | SampleAggregate, limit ?: number, precision ?: number): React.ReactNode {
            return expect<SampleAggregate>(value, v => v.avg !== undefined, v => {
                if (v.samples > 0 && v.min !== undefined && v.max !== undefined ) {
                    if (limit === undefined) limit = 1.0;
                    if (precision === undefined) precision = 1;
                    let _limit = Math.log10(limit);
                    let mleft = Math.log10(clamp(v.min, 0, limit)) / _limit * 100;
                    let mavg = Math.log10(clamp(v.avg, 0, limit)) / _limit * 100;
                    let mright = (1.0 - Math.log10(clamp(v.max, 0, limit)) / _limit) * 100;
                    return (
                        <div className="minimax">
                            <div className="current">
                                {(v.samples > 0)
                                    ? <span className="count">{v.samples}</span>
                                    : <span className="count zero">-</span>
                                }
                                {(v.current > 0)
                                    ? <span className="value">{Renderers.timing.microsLabel(v.current)}</span>
                                    : <span className="value zero">-</span>
                                }
                            </div>
                            <div className="indicator" title={`Average ${Renderers.timing.microsLabel(v.avg)}`}>
                                <span className="min">{Renderers.timing.microsLabel(v.min)}</span>
                                <div
                                    className="indicator-minmax"
                                    style={{marginLeft: `${mleft}%`, marginRight: `${mright}%`}}
                                >
                                    {(v.current === undefined)
                                        ? null  // no current value
                                        : <div
                                            className="indicator-avg"
                                            style={{marginLeft: `${mavg}%`}}
                                        />
                                    }
                                </div>
                                <span className="max">{Renderers.timing.microsLabel(v.max)}</span>
                            </div>
                        </div>
                    );
                } else if ( v.samples > 0 && v.current !== undefined ) {
                    // we dont have min/max, so we will have to indicate just the count and current
                    return (
                        <div className="totals">
                            <div className="count" title="Count">{subtle(v.samples)}</div>
                            <div className="seconds" title="Seconds">{v.current}</div>
                        </div>
                    );
                } else
                    return <div className="zero minimax">-</div>;
            });
        }
    }

    // tslint:disable-next-line:class-name
    export class timestamp {
         static locale( ts: RowLike | number ): React.ReactNode {
            return expectNumeric(ts, (v) => { return <span className="timestamp">{new Date(v * 1000).toLocaleString()}</span>; });
         }

         static moment(ts: RowLike | number ): React.ReactNode {
             return expectNumeric(ts, (v) => { return <span className="timestamp moment">{Moment.unix(v).fromNow()}</span>; });
         }

         static calendar(ts: RowLike | number): React.ReactNode {
             return expectNumeric(ts, (v) => { return <span className="timestamp moment">{Moment.unix(v).format('HH:mm:ss')}</span>; });
         }

         static brief(ts: RowLike | number): React.ReactNode {
             return expectNumeric(ts, (uts) => {
                 let _ts = Moment.unix(uts);
                 return (uts + 86400 < new Date().valueOf() / 1000)
                     ? <span className="timestamp moment">{_ts.format('lll')}</span>
                     : <span className="timestamp moment">{_ts.format('LTS')}</span>;

             });
         }

        static duration(ts: RowLike | number): React.ReactNode {
             let seconds = ts;
             // @ts-ignore
             let minutes = Math.floor(seconds / 60);
             // @ts-ignore
             seconds = seconds % 60;
             let hours = Math.floor(minutes / 60);
             minutes = minutes % 60;
             let days = Math.floor(hours / 24);
             hours = hours % 24;

             if (days !== 0) {
                 return days + 'd ' + hours + 'h ' + minutes + 'm ' + seconds + 's';
             } else {
                 return hours + 'h ' + minutes + 'm ' + seconds + 's';
             }
        }

        static durationHMS(ts: number, ms: boolean): React.ReactNode {
             let seconds = 0;
             if (ms === true) {
                 seconds = ts / 1000;
             } else {
                 seconds = ts;
             }
             // @ts-ignore
             let minutes = Math.floor(seconds / 60);
             // @ts-ignore
             seconds = seconds % 60;
             let hours = Math.floor(minutes / 60);
             minutes = minutes % 60;
             hours = hours % 24;

             let str = '';
             str += (hours > 0) ? ((hours < 10) ? '0' + hours + ':' : hours + ':') : '00:';
             str += (minutes > 0) ? ((minutes < 10) ? '0' + minutes + ':' : minutes + ':') : '00:';
             str += (seconds > 0) ? ((seconds < 10) ? '0' + seconds : seconds) : '00';

             return str;
        }

        static durationAndSince(ts: any ): React.ReactNode {
           /* return expectNumeric(ts, (v) => {
                 let _ts = Moment.unix(v);

                 let duration = _ts.fromNow();
                 let since = (v + 86400 < new Date().valueOf() / 1000)
                     ? _ts.format('lll')
                     : _ts.format('LTS');

                 return <span className="timestamp moment">{duration} {since}</span>;
                  });*/

                let utc = Math.round(+new Date() / 1000);
                let duration = utc - ts;
                let days = Math.floor(duration / 86400);
                let hours = Math.floor((duration / 3600) % 24);
                // tslint:disable-next-line:no-bitwise
                let minutes = (duration / 60) % 60 | 0;
                let seconds = duration % 60;

                let str = (days > 0) ? days + 'd ' : '';
                str += (hours > 0) ? ((hours < 10) ? '0' + hours + ':' : hours + ':') : '00:';
                str += (minutes > 0) ? ((minutes < 10) ? '0' + minutes + ':' : minutes + ':') : '00:';
                str += (seconds > 0) ? ((seconds < 10) ? '0' + seconds : seconds) : '00';

                return (
                    <div title={str}>
                        {str}
                    </div>);
         }

        static format(ts: any | number ): React.ReactNode {
             return expectNumeric(ts, (v) => {
                 if (ts === 0) {
                    return '-';
                 }

                 const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
                 ];

                 let timeInMillis = 0;
                 let newDate = new Date();
                 if (ts.toString().length === 13) { // time already in milliseconds ...
                     timeInMillis = v;
                 } else {
                     timeInMillis = (v * 1000);
                 }
                 newDate.setTime(timeInMillis);

              //   console.log('v: ',v,', offset: ', newDate.getTimezoneOffset(), ', timeInMillis: ', timeInMillis, ', newDate: ', newDate, ', hr: ', newDate.getUTCHours(), ', mi: ', newDate.getUTCMinutes());
                 const mo = (newDate.getMonth());
                 const dt = ('0' + newDate.getDate()).slice(-2);
                 const hr = ('0' + newDate.getHours()).slice(-2);
                 const mi = ('0' + newDate.getMinutes()).slice(-2);
                 const se = ('0' + newDate.getSeconds()).slice(-2);

                 let date = monthNames[mo] + ' ' + dt + ',  ' + hr + ':' + mi + ':' + se;
                 return (
                     <div>
                         {date}
                     </div>);
             });
        }
    }
    // tslint:disable-next-line:class-name
    export class network {
        static ip( value: RowLike | string): React.ReactNode {
            return expectString(value, v => {
                return <span className="ip-address">{v}</span>;
            });
        }

        static mac( value: RowLike | string): React.ReactNode {
            return expectString(value, v => {
                return <span className="mac-address">{v}</span>;
            });
        }

        static url(value: RowLike | string ): React.ReactNode {
            return expectString(value, v => {
                let url;
                try {
                    url = new URL(v);
                } catch (e) {
                    return (
                        <div className="url url-invalid">
                            <i className="fa fa-exclamation-triangle" aria-hidden="true" />
                            {v}
                        </div>
                    );
                }
                let out = Array();
                if (url.protocol === 'https:')
                    out.push( <i key="icon" className="fa fa-lock url-scheme scheme-https" aria-hidden="true" /> );
                else
                    out.push( <i key="icon" className="fa fa-unlock-alt url-scheme scheme-http" aria-hidden="true" /> );
                out.push( <span key="scheme" className={`url-scheme scheme-${url.protocol}`}>{url.protocol}//</span> );
                out.push( <span key="host" className="url-host">{url.hostname}</span> );
                out.push( <span key="path" className="url-path">{url.pathname}</span> );
                return (url.search.length === 0)
                    ? <div className="url">{out}</div>
                    : <div className="url"><h1>{out}</h1><h2>{url.search.substr(1)}</h2></div>;
            },                  v => <div key="blank" className="url blank" />);
        }
    }
    // tslint:disable-next-line:class-name
    export class percent {
        static progress(value: any): React.ReactNode {
            return expectNumeric(
                value,
                (v: number) => {
                    v = Math.min(100, v);
                    return (v === 0)
                        ? <span className="zero">*</span>
                        : (
                            <div className="percent-progress">
                                <div
                                    className={v > 66 ? 'hi' : v > 33 ? 'mid' : 'low'}
                                    style={{width: `${v}%`}}
                                />
                                <span>{v}%</span>
                            </div>
                        );
                },
                (v: any) => <div className="zero-progress" /> );
        }
    }
    // tslint:disable-next-line:class-name
    export class availability {
        static sessionPercentOverTotals(value: any): React.ReactNode {
            return expectAny(value, v => v.erroredSecs !== undefined && v.monitoredSecs !== undefined,  (v) => {
                const val = ((v.monitoredSecs - v.erroredSecs) * 100 / v.monitoredSecs);
                const valstr = (isNaN(val))
                    ? 'N/A'
                    : val.toFixed(1) + '%';
                return (
                     <span className="availability percent-over-totals" title={valstr}>
                         <h1 className="percent">{valstr}</h1>
                     </span>
                     );
            });
        }
        static win15PercentOverTotals(value: any): React.ReactNode {
            return expectAny(value, v => v.erroredSec15Win !== undefined && v.monitoredSec15Win !== undefined,  (v) => {
                const val = ((v.monitoredSec15Win - v.erroredSec15Win) * 100 / v.monitoredSec15Win);
                const valstr = (isNaN(val))
                    ? 'N/A'
                    : val.toFixed(1) + '%';
                return (
                    <span className="availability percent-over-totals" title={valstr}>
                         <h1 className="percent">{valstr}</h1>
                     </span>
                );
            });
        }
     }
    // tslint:disable-next-line:class-name
    export class veristream {
         static chicklets(value: any): React.ReactNode {
             return expectAny(
                 value,
                 (v: any) => Array.isArray(v),
                 (v: any) => {
                         let out = Array();
                         for (let x = v.length - 1; x >= 0; x--) {
                             let element = v[x];
                             if (element !== undefined) {
                                 // is element an integer (only VS number) or full chunk veristream detail?
                                 if (!isNaN(element)) {
                                     element = { vs: element };
                                 }

                                 if (element) {
                                     const vsv = Math.round(element.vs);
                                     out.push(<span key={x} className={`chicklet vs-${vsv}`}/>);
                                 }
                             }
                         }
                         return <div className="veristream"><div className="chicklets">{out}</div></div>;
                     },
                 (v: any) => <div className="veristream blank" />);
         }

         static moment(value: any): React.ReactNode {
             return expectNumeric(value, v => {
                return <span className="timestamp moment">{Moment.unix(v).fromNow()}</span>;
             });
         }
    }

    export function bitrateSingleLine( value: any ): React.ReactNode {
        return expectNumeric(value, v => {
            let unit = 'bps';
            let precision = 1;
            if (v === 0) {
                unit = '';
                precision = -1;
            } else if (v > 1024 * 1024) {
                v /= 1024 * 1024;
                unit = 'Mbps';
            } else if (v > 1024) {
                v /= 1024;
                unit = 'Kbps';
            }
            if (v < 10) precision++;
            return <span className="bitrate">{subtle(v, precision)} <span className="bitrate-unit">{unit}</span></span>;
        });
    }

    export function bitrate( value: any ): React.ReactNode {
        return expectNumeric(value, v => {
            let unit = 'bps';
            let precision = 1;
            if (v === 0) {
                unit = '';
                precision = -1;
                return <span className="bitrate bitrate-zero" />;
            } else if (v > 1024 * 1024) {
                v /= 1024 * 1024;
                unit = 'Mbps';
            } else if (v > 1024) {
                v /= 1024;
                unit = 'Kbps';
            }
            if (v < 10) precision++;
            return <span className={`bitrate bitrate-${unit}`}><h1>{v.toFixed(precision)}</h1><h2>{unit}</h2></span>;
        });
    }

    export function bytes( value: any ): React.ReactNode {
        return expectNumeric(value, v => {
            let unit = 'bytes';
            let precision = 1;
            if (v === 0) {
                unit = '';
                precision = -1;
                return <span className="bytes bytes-zero" />;
            } else if (v > 1024 * 1024 * 1024) {
                v /= 1024 * 1024 * 1024;
                unit = 'Gb';
            } else if (v > 1024 * 1024) {
                v /= 1024 * 1024;
                unit = 'Mb';
            } else if (v > 1024) {
                v /= 1024;
                unit = 'Kb';
            }
            if (v < 10) precision++;
            return <span className={`bytes bytes-${unit}`}><h1>{v.toFixed(precision)}</h1><h2>{unit}</h2></span>;
        });
    }

    export function bytesOnlyValue( value: any ): React.ReactNode {
        return expectNumeric(value, v => {
            // let unit = 'bytes';
            let precision = 1;
            if (v === 0) {
                // unit = '';
                precision = -1;
                return 0;
            } else if (v > 1024 * 1024 * 1024) {
                v /= 1024 * 1024 * 1024;
                // unit = 'Gb';
            } else if (v > 1024 * 1024) {
                v /= 1024 * 1024;
                // unit = 'Mb';
            } else if (v > 1024) {
                v /= 1024;
                // unit = 'Kb';
            }
            if (v < 10) precision++;
            return v.toFixed(precision);
        });
    }

    export function bytesToKB( value: any ): React.ReactNode {
        return expectNumeric(value, v => {
          return <span>{commaSeparated((v / 1024).toFixed(0))}</span>;
        });
    }
    export function bytesToKB2( value: any ): React.ReactNode {
        return expectNumeric(value, v => {
            return commaSeparated2((v / 1024).toFixed(0));
        });
    }
    export function secToMsec( value: any ): React.ReactNode {
        return expectNumeric(value, v => {
          return <span>{commaSeparated((v / 1000).toFixed(3))}</span>;
        });
    }
    export function secToMsec2( value: any ): React.ReactNode {
        return expectNumeric(value, v => {
            return commaSeparated2((v / 1000).toFixed(3));
        });
    }
    export function commaSeparated(value: any): React.ReactNode {
        return expectNumeric(value, v => {
          let parts = v.toString().split('.');
          parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          return <span  className="right-aligned">{parts.join('.')}</span>;
        });
    }
    export function commaSeparated2(value: any): React.ReactNode {
        return expectNumeric(value, v => {
            let parts = v.toString().split('.');
            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            return parts.join('.');
        });
    }
    export function tooltip(value: any): React.ReactNode {
        return <div title={value}>{value}</div>;
    }
    export function streamType(props: any): React.ReactNode {
        return expectString(props, (v) => {
            let longname: string;
            switch (v) {
                case 'HLS':
                    longname = 'Apple Smoothstreaming';
                    break;
                case 'HDS':
                    longname = 'Adobe HTTP Dynamic Streaming';
                    break;
                case 'DASH':
                    longname = 'Dynamic Adaptive Streaming over HTTP';
                    break;
                case 'ISS':
                    longname = 'Microsoft Smooth Streaming';
                    v = 'HSS';
                    break;
                default:
                    return <span className="stream-type">{v}</span>;
            }
            return <div className="stream-type" data-tip={longname}><h1>{v}</h1></div>;
        });
    }

    export function eventType(props: RowLike | string | ArrayLike<string> ): React.ReactNode {
        return expectString(props, v => {
            return <span className={`log-type log-type-${v.toLowerCase()}`}>{props}</span>;
        });
    }

    export function eventStatus(props: string | ArrayLike<string> ): React.ReactNode {
        return expectString(props, v => {
            return <span className={`log-status log-status-${(v as string).toLowerCase()}`}>{props}</span>;
        });
    }

    export function http(value: any, key: string ): React.ReactNode {
        return expect<Http>(
                value,
                v => !!(v && (v.hss2xx || v.hsr3xxTotal || v.hsce4xxTotal || v.hsse5xxTotal)),
                v => {
                    return (
                        <div
                            className={classNames('http totals', key)}
                            title="2xx, 3xx, 4xx, 5xx http error counts, close errors and socket errors"
                        >
                            <div className={(v.hss2xx === 0) ? 'http2xxBox-empty' : 'http2xxBox'} title="2xx">{(v.hss2xx === 0) ? '-' : commaSeparated(v.hss2xx)}</div>
                            <div className={(v.hsr3xxTotal === 0) ? 'http3xxBox-empty' : 'http3xxBox'} title="3xx">{(v.hsr3xxTotal === 0) ? '-' : commaSeparated(v.hsr3xxTotal)}</div>
                            <div className={(v.hsce4xxTotal === 0) ? 'http4xxBox-empty' : 'http4xxBox'} title="4xx">{(v.hsce4xxTotal === 0) ? '-' : commaSeparated(v.hsce4xxTotal)}</div>
                            <div className={(v.hsse5xxTotal === 0) ? 'http5xxBox-empty' : 'http5xxBox'} title="5xx">{(v.hsse5xxTotal === 0) ? '-' : commaSeparated(v.hsse5xxTotal)}</div>
                            <div className="http-close outline" title="close">{(v.close === 0) ? '-' : commaSeparated(v.close)}</div>
                            <div className="http-other outline" title="socket and other">{(v.socketErrors === 0) ? '-' : commaSeparated(v.socketErrors)}</div>
                        </div>
                    );
                });
    }

    export function totals(value: any, key: string): React.ReactNode {
        if (value.value === undefined || value.value.length === undefined) {
            return <span className={classNames('totals', key)}>-</span>;
        }

        return (
            <div
                className="totals"
            >
                <div className={(value.value[4] === 0) ? 'vs5-empty' : 'vs5'} title="vs5">{(value.value[4] === 0) ? '-' : commaSeparated(value.value[4])}</div>
                <div className={(value.value[3] === 0) ? 'vs4-empty' : 'vs4'} title="vs4">{(value.value[3] === 0) ? '-' : commaSeparated(value.value[3])}</div>
                <div className={(value.value[2] === 0) ? 'vs3-empty' : 'vs3'} title="vs3">{(value.value[2] === 0) ? '-' : commaSeparated(value.value[2])}</div>
                <div className={(value.value[1] === 0) ? 'vs2-empty' : 'vs2'} title="vs2">{(value.value[1] === 0) ? '-' : commaSeparated(value.value[1])}</div>
                <div className={(value.value[0] === 0) ? 'vs1-empty' : 'vs1'} title="vs1">{(value.value[0] === 0) ? '-' : commaSeparated(value.value[0])}</div>
            </div>
        );
    }

    export function monitorMode( value: any): React.ReactNode {
        return expectString(value, v => {
            let s: string;
            switch (v) {
                case 'HEADER_ONLY': s = 'Header Only'; break;
                case 'SAMPLE': s = 'Sample'; break;
                case 'Header Only': s = 'Header Only'; break;
                case 'Sample': s = 'Sample'; break;
                default:
                    s = 'All Segments'; break;
            }
            return <div className="monitorMode">{s}</div>;
        });
    }

    export function streamStatus(value: string): React.ReactNode {
        return expectString(value,
                            v => {
                    let substatus: string;
                    let brk = v.indexOf('-');
                    if (brk >= 0) {
                        substatus = v.substring(brk + 1).trim();
                        v = v.substring(0, brk - 1).trim();
                        return <div className="stream-status"><h1>{v}</h1><h2>{substatus}</h2></div>;
                    } else {
                        return <span className="stream-status">{v}</span>;
                    }
                },
                            v => <span className="stream-status">n/a</span>);
    }

    export function find(name: string) {
        let handler;
        let parts = name.split('.');
        if (parts.length === 0) {
            // tslint:disable-next-line:no-console
            console.log('Renderes.find expects klass.renderer-name or renderer-name, no name given');
            return undefined;
        } else if (parts.length > 2) {
            // tslint:disable-next-line:no-console
            console.log('Renderes.find must be given either klass.renderer-name or renderer-name, too many parts in ' + name);
            return undefined;
        } else if (parts.length === 2) {
            let klass = Renderers[parts[0]];
            if (klass === undefined) {
                // tslint:disable-next-line:no-console
                console.log('Renderes.find cannot find klass ' + parts[0]);
                return undefined;
            }
            handler = klass[parts[1]];
            if (handler === undefined) {
                // tslint:disable-next-line:no-console
                console.log('Renderes.find cannot find ' + name);
                return undefined;
            }
            return handler;
        }

        handler = Renderers[parts[0]];
        if (handler === undefined) {
            // tslint:disable-next-line:no-console
            console.log('Renderes.find cannot find ' + name);
            return undefined;
        }
        return handler;
    }

    export function findReactTableHandler(params: string | Array<any>) {
        if (Array.isArray(params)) {
            // pop the name
            let name = params.shift();
            let handler = find(name);
            return function(row: RowLike) {
                let args = [row.value, ...params];
                return handler.apply(null, args);
            };
        } else
            return find(params);
    }

    export function ReactTable(o: any) {
        if (Array.isArray(o)) {
            for (let i = 0, _i = o.length; i < _i; i++) {
                ReactTable(o[i]);
            }
        } else if (typeof o === 'object') {
            for (let prop in o) {
                if (!o.hasOwnProperty(prop))
                    continue;
                let value = o[prop];

                if (prop === 'type' && o.Cell === undefined) {
                    let accessor = o.accessor;
                    let handler = Renderers.findReactTableHandler(value);
                    if (handler) {
                        o.Cell = handler;
                    } else
                        // tslint:disable-next-line:no-console
                        console.log('Could not find renderer for ' + accessor);
                } else if (typeof value === 'object') {
                    ReactTable(value);
                } else if (prop === 'aggregate' && typeof value === 'string') {
                    let accessor = o.accessor, type = o.type;
                    let aggregated = null, aggregate = null;
                    switch (value) {
                        case 'blank':
                            aggregate = null;
                            aggregated = (row: any) => {return null; };
                            break;
                        case 'min-max':
                            let handler = Renderers.findReactTableHandler(type);
                            aggregate = (values: any, row: any) => (values.length > 0) ? {
                                min: _.min(values),
                                max: _.max(values)
                            } : null;
                            aggregated = (row: any) => (
                                <span className="aggregated min-max">
                                    {handler(row.value.min)} to {handler(row.value.max)}
                                </span>
                            );
                            break;
                        default:
                            // tslint:disable-next-line:no-console
                            console.log('unknown aggregation option ' + value + ' requested for field ' + accessor);
                    }

                    o.aggregate = aggregate;
                    if (aggregated)
                        o.Aggregated = aggregated;
                } else if (prop === 'align') {
                    // add alignment as a class name
                    o.className = (o.className ? o.className : '') + ' align-' + o.align;
                }
            }
        }
    }
}

export default Renderers;

/*export default class Renderers {
    constructor(props: ColumnProps)
    {
        super(props);
        console.log("testing shit");
        super({ ...props, cellRenderer:
            ({ cellData, columnData, columnIndex, dataKey, isScrolling, parent, rowData, rowIndex }) =>
                TimestampColumn.renderTimestamp(cellData, rowIndex)}
            );
    }

    static renderTimestamp(cellData : any, index: number) {
        //const {list} = this.context;
        //const row = list.get(index);
        console.log("testing");
        return <span className="timestamp">{ new Date(Number(cellData)*1000).toLocaleString() }</span>;
    };
};
*/



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/sys/columns.tsx